import styled from 'styled-components';
import { useState, FormEvent } from 'react';
import Icon from '@mdi/react';
import { mdiEmail, mdiPhone, mdiMapMarker, mdiSend, mdiCheck, mdiAlertCircle } from '@mdi/js';
import { Section, SectionHeader } from '../ui/Section';
import { GlassCard } from '../ui/GlassCard';
import { Button } from '../ui/Button';
import { Container } from '../ui/Container';
import { PageHead } from '../seo';

const PageWrapper = styled.div`
  padding-top: calc(73px + var(--space-12));
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: var(--space-8);

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div``;

const InfoCard = styled(GlassCard)`
  margin-bottom: var(--space-6);
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  margin-bottom: var(--space-6);

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const InfoContent = styled.div``;

const InfoLabel = styled.h4`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
  margin-bottom: var(--space-1);
`;

const InfoValue = styled.p`
  font-size: var(--text-base);
  color: var(--color-text-primary);
`;

const FormCard = styled(GlassCard)``;

const Form = styled.form``;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-4);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: var(--space-4);
`;

const Label = styled.label`
  display: block;
  font-size: var(--text-sm);
  font-weight: var(--font-medium);
  color: var(--color-text-primary);
  margin-bottom: var(--space-2);
`;

const Input = styled.input`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--text-base);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px rgba(81, 20, 97, 0.1);
  }

  &::placeholder {
    color: var(--color-text-tertiary);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--text-base);
  cursor: pointer;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px rgba(81, 20, 97, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--text-base);
  min-height: 150px;
  resize: vertical;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 3px rgba(81, 20, 97, 0.1);
  }

  &::placeholder {
    color: var(--color-text-tertiary);
  }
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' }>`
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-4);
  background: ${props => props.$type === 'success'
    ? 'rgba(52, 199, 89, 0.1)'
    : 'rgba(255, 59, 48, 0.1)'};
  color: ${props => props.$type === 'success'
    ? 'var(--color-success)'
    : 'var(--color-error)'};
`;

interface FormData {
  name: string;
  email: string;
  type: string;
  subject: string;
  message: string;
}

export function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    type: 'general',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const res = await fetch('https://portfolio.data26.solutions/api/leads/inbound', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          source: 'website',
          sourceDetails: 'portfolio.data26.solutions',
          campaign: formData.type,
          description: `${formData.subject.trim()}\n\n${formData.message.trim()}`,
          submitted_from: 'portfolio.data26.solutions/contact',
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error || `HTTP ${res.status}`);
      }

      setStatus('success');
      setFormData({
        name: '',
        email: '',
        type: 'general',
        subject: '',
        message: '',
      });
    } catch (error) {
      setStatus('error');
      const msg = error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
      setErrorMessage(msg);
      console.error('Error submitting form:', error);
    }
  };

  return (
    <PageWrapper>
      <PageHead
        title="Contact Us"
        description="Have a project in mind or feedback on our apps? Get in touch — we typically respond within 24 hours."
      />
      <Section>
        <Container>
          <SectionHeader
            title="Get in Touch"
            subtitle="Have a project in mind or feedback on our apps? We'd love to hear from you."
          />

          <ContactGrid>
            <ContactInfo>
              <InfoCard>
                <InfoItem>
                  <InfoIcon>
                    <Icon path={mdiPhone} size={1} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Phone</InfoLabel>
                    <InfoValue>
                      <a href="tel:717-448-8377" style={{ color: 'inherit', textDecoration: 'none' }}>
                        (717) 448-8377
                      </a>
                    </InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon>
                    <Icon path={mdiEmail} size={1} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Email</InfoLabel>
                    <InfoValue>
                      <a href="mailto:sales@lancastersystemsautomation.com" style={{ color: 'inherit', textDecoration: 'none' }}>
                        sales@lancastersystemsautomation.com
                      </a>
                    </InfoValue>
                  </InfoContent>
                </InfoItem>

                <InfoItem>
                  <InfoIcon>
                    <Icon path={mdiMapMarker} size={1} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Location</InfoLabel>
                    <InfoValue>United States</InfoValue>
                  </InfoContent>
                </InfoItem>
              </InfoCard>

              <GlassCard variant="subtle">
                <h4 style={{ marginBottom: 'var(--space-3)' }}>Contact Types</h4>
                <p style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
                  <strong>Hire Us:</strong> Start a conversation about your project needs.
                </p>
                <p style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
                  <strong>App Feedback:</strong> Share thoughts on Charque, Inventory, When, or Bestagon.
                </p>
                <p style={{ fontSize: 'var(--text-sm)' }}>
                  <strong>General:</strong> Anything else you'd like to discuss.
                </p>
              </GlassCard>
            </ContactInfo>

            <FormCard>
              {status === 'success' && (
                <StatusMessage $type="success">
                  <Icon path={mdiCheck} size={1} />
                  Thanks for reaching out! We'll get back to you soon.
                </StatusMessage>
              )}

              {status === 'error' && (
                <StatusMessage $type="error">
                  <Icon path={mdiAlertCircle} size={1} />
                  {errorMessage}
                </StatusMessage>
              )}

              <Form onSubmit={handleSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="type">Contact Type</Label>
                    <Select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                    >
                      <option value="general">General Inquiry</option>
                      <option value="hire">Hire Us</option>
                      <option value="app-feedback">App Feedback</option>
                      <option value="support">Support</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label htmlFor="message">Message</Label>
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more..."
                    required
                  />
                </FormGroup>

                <Button
                  type="submit"
                  fullWidth
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? 'Sending...' : 'Send Message'}
                  <Icon path={mdiSend} size={0.8} />
                </Button>
              </Form>
            </FormCard>
          </ContactGrid>
        </Container>
      </Section>
    </PageWrapper>
  );
}

export default Contact;
